const SERVICE_UUID = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
const WRITE_UUID = '49535343-8841-43f4-a8d4-ecbe34729bb3';
const NOTIFY_UUID = '49535343-1e4d-4bd9-ba61-23c647249616';
const NAME_PREFIX = 'Printer_';

const grantedDevices = new Map();
const preferredNames = new Map();
const ADVERTISEMENT_WAIT_MS = 3000;

/**
 * Chrome guarda el id en base64. Un `+` del formulario puede volver como espacio.
 *
 * @param {string} id
 * @returns {string}
 */
function normalizeBluetoothId(id) {
    return String(id || '').trim().replace(/ /g, '+');
}

/**
 * Sin id no hay impresora configurada: hay que buscarla por BLE.
 *
 * @param {string} id
 * @returns {boolean}
 */
function missingBluetoothId(id) {
    const value = id == null ? '' : String(id).trim();
    return value === '' || value === 'null';
}

/**
 * @param {string} left
 * @param {string} right
 * @returns {boolean}
 */
function sameDeviceId(left, right) {
    const wanted = normalizeBluetoothId(left);
    return wanted !== '' && wanted === normalizeBluetoothId(right);
}

/**
 * @param {BluetoothDevice} device
 * @returns {boolean}
 */
function isGprinterName(device) {
    return !!(device && typeof device.name === 'string' && device.name.indexOf(NAME_PREFIX) === 0);
}

/**
 * @returns {Bluetooth | null}
 */
function browserBluetooth() {
    if (typeof navigator === 'undefined' || !navigator.bluetooth) {
        return null;
    }
    return navigator.bluetooth;
}

/**
 * @param {Bluetooth | null | undefined} bluetooth
 * @returns {boolean}
 */
function webBluetoothSupported(bluetooth) {
    const api = bluetooth === undefined ? browserBluetooth() : bluetooth;
    return !!(api && typeof api.requestDevice === 'function');
}

/**
 * El id de Chrome solo vale en este origen. Se guarda para reconectar sin diálogo.
 *
 * @param {BluetoothDevice} device
 */
function rememberGrantedDevice(device) {
    if (device && device.id) {
        grantedDevices.set(device.id, device);
        grantedDevices.set(normalizeBluetoothId(device.id), device);
    }
}

/**
 * El nombre BLE sobrevive al refresco aunque Chrome cambie el id opaco.
 *
 * @param {string} id
 * @param {string} name
 */
function rememberPrinterName(id, name) {
    const key = normalizeBluetoothId(id);
    if (key && typeof name === 'string' && name.indexOf(NAME_PREFIX) === 0) {
        preferredNames.set(key, name);
    }
}

/**
 * Chrome puede reconectar sin diálogo solo si existe getDevices.
 *
 * @param {Bluetooth | null | undefined} [bluetooth]
 * @returns {boolean}
 */
function canListGrantedDevices(bluetooth) {
    const api = bluetooth === undefined ? browserBluetooth() : bluetooth;
    return !!(api && typeof api.getDevices === 'function');
}

/**
 * @param {string} id
 * @returns {boolean}
 */
function hasGrantedDevice(id) {
    return !!cachedDevice(id);
}

/**
 * Hay que llamarlo en el mismo click: un await previo anula el gesto.
 *
 * @param {Bluetooth | null | undefined} [bluetooth]
 * @returns {Promise<BluetoothDevice>}
 */
function requestGprinterDevice(bluetooth) {
    const api = bluetooth === undefined ? browserBluetooth() : bluetooth;
    if (!webBluetoothSupported(api)) {
        return Promise.reject(new Error('bluetooth_off'));
    }
    return api.requestDevice({
        filters: [{ namePrefix: NAME_PREFIX }],
        optionalServices: [SERVICE_UUID]
    }).then(function (device) {
        rememberGrantedDevice(device);
        return device;
    });
}

/**
 * @param {Bluetooth | null | undefined} api
 * @param {string} id
 * @returns {Promise<BluetoothDevice | null>}
 */
function cachedDevice(id) {
    return grantedDevices.get(id) || grantedDevices.get(normalizeBluetoothId(id)) || null;
}

/**
 * @param {BluetoothDevice[]} named
 * @param {string} id
 * @returns {BluetoothDevice | null}
 */
function chooseNamedDevice(named, id) {
    const preferred = preferredNames.get(normalizeBluetoothId(id));
    if (preferred) {
        for (let index = 0; index < named.length; index += 1) {
            if (named[index].name === preferred) {
                return named[index];
            }
        }
    }
    if (named.length === 1) {
        return named[0];
    }
    return null;
}

function findGrantedDevice(api, id) {
    log('[GPrinter] busco dispositivo autorizado', id);
    const cached = cachedDevice(id);
    if (cached) {
        log('[GPrinter] uso el dispositivo en memoria', cached.id, cached.name);
        return Promise.resolve(cached);
    }
    if (!api || typeof api.getDevices !== 'function') {
        log('[GPrinter] este navegador no tiene getDevices');
        return Promise.resolve(null);
    }
    return api.getDevices().then(function (devices) {
        const list = Array.isArray(devices) ? devices : [];
        log('[GPrinter] getDevices', list.map(function (device) {
            return { id: device && device.id, name: device && device.name };
        }));
        const named = [];
        for (let index = 0; index < list.length; index += 1) {
            const device = list[index];
            if (device && sameDeviceId(device.id, id)) {
                log('[GPrinter] el id guardado coincide', device.id, device.name);
                rememberGrantedDevice(device);
                grantedDevices.set(id, device);
                return device;
            }
            if (isGprinterName(device)) {
                named.push(device);
            }
        }
        const chosen = chooseNamedDevice(named, id);
        if (!chosen) {
            log('[GPrinter] ningún dispositivo autorizado coincide', id);
            return null;
        }
        log('[GPrinter] uso la impresora autorizada', chosen.id, chosen.name);
        rememberGrantedDevice(chosen);
        grantedDevices.set(id, chosen);
        grantedDevices.set(normalizeBluetoothId(id), chosen);
        return chosen;
    });
}

/**
 * @param {BluetoothDevice} device
 */
function stopWatching(device) {
    if (!device || typeof device.unwatchAdvertisements !== 'function') {
        return;
    }
    try {
        device.unwatchAdvertisements();
    } catch (error) {
        return;
    }
}

/**
 * En macOS, tras refrescar, el primer gatt.connect falla hasta ver un anuncio.
 *
 * @param {BluetoothDevice} device
 * @returns {Promise<void>}
 */
function waitForAdvertisement(device) {
    if (!device || typeof device.watchAdvertisements !== 'function') {
        return Promise.resolve();
    }
    return new Promise(function (resolve) {
        let settled = false;
        const finish = function () {
            if (settled) {
                return;
            }
            settled = true;
            resolve();
        };
        const onAdvertisement = function () {
            clearTimeout(timer);
            stopWatching(device);
            device.removeEventListener('advertisementreceived', onAdvertisement);
            finish();
        };
        const timer = setTimeout(function () {
            stopWatching(device);
            device.removeEventListener('advertisementreceived', onAdvertisement);
            finish();
        }, ADVERTISEMENT_WAIT_MS);
        device.addEventListener('advertisementreceived', onAdvertisement);
        device.watchAdvertisements().catch(function () {
            clearTimeout(timer);
            stopWatching(device);
            device.removeEventListener('advertisementreceived', onAdvertisement);
            finish();
        });
    });
}

/**
 * @param {BluetoothDevice} device
 * @returns {Promise<BluetoothRemoteGATTServer>}
 */
function connectGatt(device) {
    const once = function () {
        log('[GPrinter] gatt.connect', device && device.id, device && device.name);
        return device.gatt.connect();
    };
    return once().catch(function (error) {
        log('[GPrinter] gatt.connect falló, espero anuncio y reintento', error && error.message);
        return waitForAdvertisement(device).then(once);
    });
}

/**
 * @param {BluetoothDevice} device
 * @param {Map<string, object>} sessions
 * @returns {Promise<void>}
 */
function keepSession(sessions, session, requestedId) {
    sessions.set(session.device.id, session);
    if (requestedId) {
        sessions.set(requestedId, session);
        sessions.set(normalizeBluetoothId(requestedId), session);
    }
}

function sessionFor(sessions, id) {
    return sessions.get(id) || sessions.get(normalizeBluetoothId(id)) || null;
}

function openSession(device, sessions, requestedId) {
    return connectGatt(device).then(function (server) {
        const gattServer = server || device.gatt;
        return gattServer.getPrimaryService(SERVICE_UUID).then(function (service) {
            return Promise.all([
                service.getCharacteristic(WRITE_UUID),
                service.getCharacteristic(NOTIFY_UUID)
            ]).then(function (characteristics) {
                keepSession(sessions, {
                    device: device,
                    writeChar: characteristics[0],
                    notifyChar: characteristics[1],
                    listener: null
                }, requestedId);
            });
        });
    }).catch(function (error) {
        if (device.gatt && device.gatt.connected && typeof device.gatt.disconnect === 'function') {
            device.gatt.disconnect();
        }
        return Promise.reject(error);
    });
}

/**
 * Misma forma de callbacks que cordova-plugin-ble-central.
 *
 * @param {Bluetooth | null | undefined} [bluetooth]
 * @returns {object}
 */
function createWebBluetoothAdapter(bluetooth) {
    const api = bluetooth === undefined ? browserBluetooth() : bluetooth;
    const sessions = new Map();

    return {
        isEnabled: function (success, failure) {
            if (!api) {
                failure();
                return;
            }
            if (typeof api.getAvailability !== 'function') {
                success();
                return;
            }
            api.getAvailability().then(function (available) {
                if (available) {
                    success();
                    return;
                }
                failure();
            }).catch(function () {
                failure();
            });
        },

        connect: function (id, success, failure) {
            log('[GPrinter] web connect', id);
            let settled = false;
            const finish = function (ok) {
                if (settled) {
                    return;
                }
                settled = true;
                if (ok) {
                    success();
                    return;
                }
                failure();
            };

            findGrantedDevice(api, id).then(function (device) {
                if (!device || !device.gatt) {
                    log('[GPrinter] web connect sin dispositivo', id);
                    finish(false);
                    return null;
                }
                return openSession(device, sessions, id).then(function () {
                    log('[GPrinter] web connect ok', device.id);
                    finish(true);
                });
            }).catch(function (error) {
                log('[GPrinter] web connect error', error && error.message);
                finish(false);
            });
        },

        disconnect: function (id, success) {
            const session = sessionFor(sessions, id);
            const keys = [];
            sessions.forEach(function (value, key) {
                if (value === session) {
                    keys.push(key);
                }
            });
            keys.forEach(function (key) {
                sessions.delete(key);
            });
            if (session && session.device.gatt && session.device.gatt.connected) {
                session.device.gatt.disconnect();
            }
            success();
        },

        write: function (id, service, characteristic, data, success, failure) {
            const session = sessionFor(sessions, id);
            if (!session) {
                failure();
                return;
            }
            const payload = data instanceof ArrayBuffer ? data : data.buffer;
            session.writeChar.writeValue(payload).then(function () {
                success();
            }).catch(function () {
                failure();
            });
        },

        startNotification: function (id, service, characteristic, success, failure, options) {
            const session = sessionFor(sessions, id);
            if (!session) {
                failure();
                return;
            }
            const listener = function (event) {
                const value = event && event.target ? event.target.value : null;
                success(value || new DataView(new ArrayBuffer(0)));
            };
            session.listener = listener;
            session.notifyChar.addEventListener('characteristicvaluechanged', listener);
            session.notifyChar.startNotifications().then(function () {
                if (options && options.emitOnRegistered) {
                    success('registered');
                }
            }).catch(function () {
                failure();
            });
        },

        stopNotification: function (id, service, characteristic, success) {
            const session = sessionFor(sessions, id);
            if (session && session.listener) {
                session.notifyChar.removeEventListener('characteristicvaluechanged', session.listener);
                session.listener = null;
            }
            const stop = session && session.notifyChar.stopNotifications
                ? session.notifyChar.stopNotifications()
                : Promise.resolve();
            stop.then(function () {
                success();
            }).catch(function () {
                success();
            });
        },

        scan: function (services, seconds, success, failure) {
            if (typeof failure === 'function') {
                failure();
            }
        },

        stopScan: function (success) {
            if (typeof success === 'function') {
                success();
            }
        }
    };
}

export {
    NAME_PREFIX,
    SERVICE_UUID,
    createWebBluetoothAdapter,
    missingBluetoothId,
    canListGrantedDevices,
    hasGrantedDevice,
    rememberGrantedDevice,
    rememberPrinterName,
    requestGprinterDevice,
    webBluetoothSupported
};
